import React, { useEffect, useState } from 'react';
import axios from 'axios';
import SrtBarGraph from './SrtBarGraph';
import WrsLineGraph from './WrsLineGraph';
import PbMaxBarGraph from './PbMaxBarGraph';
import {
  normaliseSpeech, autoPbMax, wrsClass, buildClinicalInterpretation, buildRecommendations,
} from './speechLogic';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND = {
  primary:     '#1a56db',
  primarySoft: '#e6efff',
  tableHead:   '#dbeafe',
  tableAlt:    '#f8fafc',
  border:      '#cbd5e1',
  textMuted:   '#64748b',
};

const SpeechReportView = ({ state, patient, clinic, audiologistName, license, sessionId }) => {
  const s = state;
  const printedOn = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const clin = s.clinical_interpretation || buildClinicalInterpretation(s);
  const recs = s.recommendations || buildRecommendations();

  const InfoCell = ({ label, value }) => (
    <div className="flex text-[9.5px] leading-tight">
      <div className="w-2/5 font-semibold" style={{ color: BRAND.textMuted }}>{label}</div>
      <div className="flex-1" style={{ color: '#0f172a' }}>: {value || '—'}</div>
    </div>
  );

  const pbR = (s.pb_max?.right !== null && s.pb_max?.right !== undefined) ? Number(s.pb_max.right) : autoPbMax(s, 'right');
  const pbL = (s.pb_max?.left  !== null && s.pb_max?.left  !== undefined) ? Number(s.pb_max.left)  : autoPbMax(s, 'left');

  return (
    <div id="speech-report-canvas" data-testid="speech-report-canvas"
      className="speech-report bg-white text-slate-900"
      style={{ width: '210mm', minHeight: '297mm', padding: '8mm', boxSizing: 'border-box', fontFamily: 'Arial, Helvetica, sans-serif', color: '#0f172a', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}
    >
      {/* Letterhead */}
      <div className="flex items-center justify-between border-b pb-1 mb-1.5" style={{ borderColor: BRAND.border }}>
        <div className="flex items-center gap-2">
          {clinic?.logo_base64 && <img src={clinic.logo_base64} alt="logo" style={{ width: 52, height: 52, objectFit: 'contain' }} />}
          <div>
            <div className="text-[16px] font-black" style={{ color: BRAND.primary, letterSpacing: 0.5 }}>{clinic?.name || 'audinexa'}</div>
            {clinic?.tagline && <div className="text-[9px] italic" style={{ color: BRAND.textMuted }}>{clinic.tagline}</div>}
          </div>
        </div>
        <div className="text-center">
          <div className="text-[10px] font-bold">Smarter Audiology.</div>
          <div className="text-[10px] font-bold">Healthier Hearing Futures.</div>
        </div>
        <div className="text-right" style={{ color: BRAND.primary, fontFamily: 'cursive' }}>
          <div className="text-[13px] italic font-semibold">Better Care</div>
          <div className="text-[13px] italic font-semibold">Brighter Lives</div>
        </div>
      </div>

      {/* Title band */}
      <div className="text-center py-1.5 rounded" style={{ backgroundColor: BRAND.primarySoft }} data-testid="speech-report-title-band">
        <div className="text-[16px] font-black uppercase" style={{ color: BRAND.primary, letterSpacing: 1 }}>Speech Audiometry Report</div>
        <div className="text-[10px] font-bold tracking-widest" style={{ color: BRAND.primary }}>UNDERSTANDING WORDS. IMPROVING LIVES.</div>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-3 gap-2 mt-2 border rounded px-2 py-1.5" style={{ borderColor: BRAND.border }} data-testid="speech-report-info">
        <div className="space-y-0.5">
          <InfoCell label="Patient Name" value={patient?.name} />
          <InfoCell label="Patient ID" value={patient?.mrd || patient?.patient_id} />
          <InfoCell label="Age / Gender" value={patient?.age ? `${patient.age} years / ${patient.gender ? patient.gender[0]?.toUpperCase() + patient.gender.slice(1) : ''}` : ''} />
          <InfoCell label="Date of Test" value={printedOn} />
          <InfoCell label="Refer by" value={s.refer_by} />
        </div>
        <div className="space-y-0.5">
          <InfoCell label="Clinic Name" value={clinic?.name} />
          <InfoCell label="Clinician" value={s.clinician || audiologistName} />
          <InfoCell label="Test Environment" value={s.environment} />
          <InfoCell label="Transducer" value={s.transducer} />
        </div>
        <div className="space-y-0.5">
          <InfoCell label="Test Material" value={s.test_material} />
          <InfoCell label="Presentation" value={s.presentation} />
          <InfoCell label="Standard" value={s.standard} />
        </div>
      </div>

      {/* Top row: SRT (left half) | WRS (right half) */}
      <div className="grid grid-cols-2 gap-2 mt-2" data-testid="speech-report-top-row">
        <div className="border rounded" style={{ borderColor: BRAND.border }} data-testid="speech-report-srt">
          <div className="px-2 py-1 font-bold text-[11px]" style={{ color: BRAND.primary, backgroundColor: BRAND.primarySoft }}>Speech Reception Threshold (SRT)</div>
          <div className="px-2 pt-1 pb-2">
            <SrtBarGraph srt={s.srt} readOnly width={330} height={190} />
          </div>
        </div>
        <div className="border rounded" style={{ borderColor: BRAND.border }} data-testid="speech-report-wrs">
          <div className="px-2 py-1 font-bold text-[11px]" style={{ color: BRAND.primary, backgroundColor: BRAND.primarySoft }}>Word Recognition Score (WRS)</div>
          <div className="px-2 pt-1 pb-2">
            <WrsLineGraph wrs={s.wrs} intensities={s.wrs_rows} readOnly width={340} height={190} />
          </div>
        </div>
      </div>

      {/* Middle row: PB Max graph (left) | SRT + PB Max tables (centre) | WRS table (right) */}
      <div className="grid gap-2 mt-2" style={{ gridTemplateColumns: '1fr 1fr 1fr' }} data-testid="speech-report-mid-row">
        <div className="border rounded" style={{ borderColor: BRAND.border }} data-testid="speech-report-pbmax">
          <div className="px-2 py-1 font-bold text-[11px]" style={{ color: BRAND.primary, backgroundColor: BRAND.primarySoft }}>Speech Discrimination (PB Max)</div>
          <div className="px-2 pt-1 pb-2">
            <PbMaxBarGraph state={s} readOnly width={220} height={180} />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="border rounded" style={{ borderColor: BRAND.border }} data-testid="speech-report-srt-table">
            <div className="px-2 py-1 font-bold text-[11px]" style={{ color: BRAND.primary, backgroundColor: BRAND.primarySoft }}>SRT Results</div>
            <table className="w-full text-[10px]" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: BRAND.tableHead }}>
                  <th className="border px-1 py-0.5" style={{ borderColor: BRAND.border }}>Ear</th>
                  <th className="border px-1 py-0.5" style={{ borderColor: BRAND.border }}>SRT (dB HL)</th>
                </tr>
              </thead>
              <tbody>
                {['right', 'left'].map((ear, i) => (
                  <tr key={ear} style={{ backgroundColor: i % 2 ? BRAND.tableAlt : '#ffffff' }}>
                    <td className="border px-1 py-0.5" style={{ borderColor: BRAND.border, color: ear === 'right' ? '#dc2626' : '#2563eb', fontWeight: 700 }}>{ear === 'right' ? 'Right Ear (RE)' : 'Left Ear (LE)'}</td>
                    <td className="border px-1 py-0.5 text-center font-mono" style={{ borderColor: BRAND.border }}>{s.srt[ear] ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border rounded" style={{ borderColor: BRAND.border }} data-testid="speech-report-pbmax-table">
            <div className="px-2 py-1 font-bold text-[11px]" style={{ color: BRAND.primary, backgroundColor: BRAND.primarySoft }}>Speech Discrimination (PB Max)</div>
            <table className="w-full text-[10px]" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: BRAND.tableHead }}>
                  <th className="border px-1 py-0.5" style={{ borderColor: BRAND.border }}>Ear</th>
                  <th className="border px-1 py-0.5" style={{ borderColor: BRAND.border }}>Score (%)</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ backgroundColor: '#ffffff' }}>
                  <td className="border px-1 py-0.5" style={{ borderColor: BRAND.border, color: '#dc2626', fontWeight: 700 }}>Right Ear (RE)</td>
                  <td className="border px-1 py-0.5 text-center font-mono" style={{ borderColor: BRAND.border }}>{pbR ?? '—'}</td>
                </tr>
                <tr style={{ backgroundColor: BRAND.tableAlt }}>
                  <td className="border px-1 py-0.5" style={{ borderColor: BRAND.border, color: '#2563eb', fontWeight: 700 }}>Left Ear (LE)</td>
                  <td className="border px-1 py-0.5 text-center font-mono" style={{ borderColor: BRAND.border }}>{pbL ?? '—'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="border rounded" style={{ borderColor: BRAND.border }} data-testid="speech-report-wrs-table">
          <div className="px-2 py-1 font-bold text-[11px]" style={{ color: BRAND.primary, backgroundColor: BRAND.primarySoft }}>WRS Results</div>
          <table className="w-full text-[10px]" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: BRAND.tableHead }}>
                <th className="border px-1 py-0.5" style={{ borderColor: BRAND.border }}>Intensity (dB HL)</th>
                <th className="border px-1 py-0.5" style={{ borderColor: BRAND.border, color: '#dc2626' }}>Right Ear (%)</th>
                <th className="border px-1 py-0.5" style={{ borderColor: BRAND.border, color: '#2563eb' }}>Left Ear (%)</th>
              </tr>
            </thead>
            <tbody>
              {s.wrs_rows.map((db, i) => (
                <tr key={db} style={{ backgroundColor: i % 2 ? BRAND.tableAlt : '#ffffff' }}>
                  <td className="border px-1 py-0.5 text-center font-mono" style={{ borderColor: BRAND.border, fontWeight: 700 }}>{db}</td>
                  <td className="border px-1 py-0.5 text-center font-mono" style={{ borderColor: BRAND.border }}>{s.wrs.right[String(db)] ?? '—'}</td>
                  <td className="border px-1 py-0.5 text-center font-mono" style={{ borderColor: BRAND.border }}>{s.wrs.left[String(db)] ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Clinical Interpretation */}
      <div className="mt-3" data-testid="speech-report-clinical">
        <div className="text-[11px] font-bold px-2 py-1 rounded" style={{ color: BRAND.primary, backgroundColor: BRAND.primarySoft }}>Clinical Interpretation</div>
        <div className="mt-1 text-[10px] px-2" style={{ color: '#0f172a' }}>
          {clin.split(/\n+/).filter(Boolean).map((line, i) => (
            <div key={i} className="flex gap-1"><span style={{ color: BRAND.primary }}>•</span><span>{line.replace(/^[•\-*]\s*/, '')}</span></div>
          ))}
        </div>
      </div>

      {/* Recommendations */}
      <div className="mt-2" data-testid="speech-report-recs">
        <div className="text-[11px] font-bold px-2 py-1 rounded" style={{ color: BRAND.primary, backgroundColor: BRAND.primarySoft }}>Recommendations</div>
        <div className="mt-1 text-[10px] px-2" style={{ color: '#0f172a' }}>
          {recs.split(/\n+/).filter(Boolean).map((line, i) => (
            <div key={i} className="flex gap-1"><span style={{ color: BRAND.primary }}>•</span><span>{line.replace(/^[•\-*]\s*/, '')}</span></div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-end justify-between mt-3 pt-2 border-t" style={{ borderColor: BRAND.border }} data-testid="speech-report-footer">
        <div>
          <div className="text-[11px] font-bold" style={{ color: BRAND.primary }}>{clinic?.name || 'Audinexa Hearing Clinic'}</div>
          <div className="text-[9px]" style={{ color: BRAND.textMuted }}>📍 {clinic?.address_line1}{clinic?.address_line2 ? `, ${clinic.address_line2}` : ''}</div>
          <div className="text-[9px]" style={{ color: BRAND.textMuted }}>
            {clinic?.tel && <>📞 {clinic.tel}&nbsp;&nbsp;</>}{clinic?.email && <>✉ {clinic.email}</>}
          </div>
        </div>
        <div className="text-center text-[10px] italic" style={{ color: '#334155' }}>
          <div>Technology for Today.</div>
          <div>Better Hearing for Tomorrow.</div>
        </div>
        <div className="text-right">
          <div className="border-t pt-0.5 min-w-[140px]" style={{ borderColor: '#475569' }}>
            <div className="font-bold text-[11px]">{audiologistName || 'Audiologist'}</div>
            <div className="text-[9px]" style={{ color: BRAND.textMuted }}>Audiologist</div>
            {license && <div className="text-[9px]" style={{ color: BRAND.textMuted }}>Reg. No.: {license}</div>}
          </div>
        </div>
      </div>
      {sessionId && <div className="mt-1 text-[8px] text-right" style={{ color: BRAND.textMuted }}>Ref: {sessionId}</div>}
    </div>
  );
};

export const SpeechReportViewWithFetch = ({ state, patient, sessionId, audiologistName }) => {
  const [clinic, setClinic] = useState({ name: '', tagline: '', address_line1: '', address_line2: '', tel: '', email: '', logo_base64: null });
  const [license, setLicense] = useState('');
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/settings/clinic${sessionId ? `?session_id=${sessionId}` : ''}`);
        const c = r.data || {};
        if (!alive) return;
        setClinic({
          name: c.name || '', tagline: c.tagline || '',
          address_line1: c.address || '',
          address_line2: [c.city, c.state, c.pincode].filter(Boolean).join(', '),
          tel: c.phone || '', email: c.email || '', logo_base64: null,
        });
        try {
          const lr = await axios.get(`${API}/settings/clinic/logo`, { responseType: 'blob' });
          if (alive) setClinic((prev) => ({ ...prev, logo_base64: URL.createObjectURL(lr.data) }));
        } catch { /* no logo */ }
      } catch { /* fallback */ }
      try {
        if (sessionId) {
          const sr = await axios.get(`${API}/sessions/${sessionId}`);
          const uid = sr.data?.audiologist_user_id;
          if (uid && alive) {
            const lr = await axios.get(`${API}/settings/users/${uid}/signature`, { responseType: 'blob' });
            const lic = lr?.headers?.['x-license-no'] || lr?.headers?.['X-License-No'] || '';
            if (alive) setLicense(lic);
          }
        }
      } catch { /* no license */ }
    })();
    return () => { alive = false; };
  }, [sessionId]);

  return (
    <SpeechReportView state={state} patient={patient} clinic={clinic} audiologistName={audiologistName} license={license} sessionId={sessionId} />
  );
};

export default SpeechReportView;
